// ChaosMeter: Visualizes the world's chaos status and user's sealed techniques
import { useEffect, useState } from "react";

interface ChaosMeterProps {
  sealed: number; // User's sealed techniques
  total: number; // Total techniques available
  globalRestored: number; // Global % of Mathematical Order restored (0-100)
}

export function ChaosMeter({ sealed, total, globalRestored }: ChaosMeterProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const userProgress = total > 0 ? (sealed / total) * 100 : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border-2 border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        World Chaos Status
      </h3>

      {/* Visual Chaos Meter */}
      <div className="relative mb-6">
        <div className="h-8 bg-gray-900 dark:bg-gray-950 rounded-lg overflow-hidden relative">
          {/* Restored portion (white) */}
          <div
            className={`h-full bg-gradient-to-r from-gray-100 to-white dark:from-gray-300 dark:to-gray-100 transition-all duration-1000 ease-out ${mounted ? "opacity-100" : "opacity-0"}`}
            style={{ width: `${globalRestored}%` }}
          />

          {/* Chaos pulse animation on active chaos region */}
          <div
            className="absolute top-0 left-0 h-full bg-black opacity-20 animate-chaos-pulse pointer-events-none"
            style={{ width: `${100 - globalRestored}%`, left: `${globalRestored}%` }}
          />
        </div>

        {/* Labels */}
        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-2">
          <span>🌑 Chaos</span>
          <span>☀️ Order Restored</span>
        </div>
      </div>

      {/* User Stats */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            You've sealed:
          </span>
          <span className="text-lg font-bold text-gray-900 dark:text-white">
            {sealed}/{total}
          </span>
        </div>

        {/* User Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-1000 ease-out ${mounted ? "opacity-100" : "opacity-0"}`}
            style={{ width: `${userProgress}%` }}
          />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Global Progress:
          </span>
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
            {Math.round(globalRestored)}% restored
          </span>
        </div>
      </div>

      {/* Motivational Message */}
      {sealed === 0 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-xs text-blue-800 dark:text-blue-200">
            Begin your journey by mastering Ancient Forms to seal Chaos Sources!
          </p>
        </div>
      )}
      {sealed > 0 && sealed < total / 2 && (
        <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="text-xs text-purple-800 dark:text-purple-200">
            The battle continues! Each sealed source brings order to the world.
          </p>
        </div>
      )}
      {sealed >= total / 2 && sealed < total && (
        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            Over halfway! You're a formidable Mind Warrior!
          </p>
        </div>
      )}
      {sealed === total && (
        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-xs text-green-800 dark:text-green-200">
            🎉 All Chaos Sources sealed! Mathematical Order restored!
          </p>
        </div>
      )}
    </div>
  );
}
